import _, { isNull, map, isEmpty } from "lodash";
import React, { useState, useEffect, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { t } from "i18next";

import { Icon, BadgeSwitch, ProgressiveImage } from "components";
import { renderMascot } from "utils";
import {
  getIsMaximized,
  getButtonBgColor,
  setJaiEditUrl,
  selectPropertyId,
  setFocusedPhotoIdx,
  setIsPhotoTourScreen,
  getJaiEditUrl,
  selectIsJAI,
  getArtieLogo,
  setGalleries,
  setBadgeSwitchPhotos,
  setSelectedPhotoSwitchId,
} from "slices";

import ExtraOption from "../ExtraOptions";

const GalleryView = memo(({ showButtons = true, message, isUserMsg, staticImageData, customSize, position, mascotSettings, compMsgMarginClass, images }) => {
  const dispatch = useDispatch();
  const isMaximized = useSelector(getIsMaximized);
  const artieLogo = useSelector(getArtieLogo);
  const [selectedBadgeIdL, setSelectedBadgeIdL] = useState(null);
  const [selectedBadgeItemL, setSelectedBadgeItemL] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const color = useSelector(getButtonBgColor);
  const propertyId = useSelector(selectPropertyId);
  const jaiEditURL = useSelector(getJaiEditUrl);
  const isJAI = useSelector(selectIsJAI);

  const badgeItems = _(images)
    .keys()
    .filter((k) => !_.isEmpty(images[k]))
    .map((k) => ({ id: k.toLowerCase(), title: k }))
    .valueOf();

  const [galleryItems, setGalleryItems] = useState([]);

  const configureEditUrl = () => {
    const { id } = selectedBadgeItemL;

    switch (id) {
      case "interior":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/gallery_interior`));
        break;
      case "exterior":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/gallery_exterior`));
        break;
      case "amenities":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/gallery_amenities`));
        break;
      case "neighborhood":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/gallery_neighborhood`));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!isNull(galleryImages) && !isNull(selectedBadgeItemL)) {
      setGalleryItems(map(galleryImages[selectedBadgeIdL]));
      configureEditUrl();
    }
  }, [galleryImages, selectedBadgeItemL, selectedBadgeIdL]);

  useEffect(() => {
    setGalleryImages(images);
    setSelectedBadgeItemL(badgeItems[0]);
    setSelectedBadgeIdL(badgeItems[0].id.toLowerCase());
  }, [images]);

  if (galleryItems.length < 1) {
    return null;
  }

  const handleClick = (idx, d) => {
    dispatch(setGalleries(images));

    dispatch(
      setBadgeSwitchPhotos(
        _(images)
          .keys()
          .filter((k) => !_.isEmpty(images[k]))
          .map((k) => ({ id: k.toLowerCase(), title: k }))
          .valueOf()
      )
    );

    dispatch(setSelectedPhotoSwitchId(selectedBadgeIdL));
    dispatch(setFocusedPhotoIdx(idx));
    dispatch(setIsPhotoTourScreen(true));
  };

  const dataArr = isMaximized ? galleryItems.slice(0, 16) : galleryItems.slice(0, 9);
  const maxLength = isMaximized ? 16 : 9;

  const buttons =
    galleryItems?.length > maxLength
      ? [
          {
            text: t("seeAllPhotos", { count: galleryItems.length }),
            handleClick: () => handleClick(0, galleryItems[0]),
          },
        ]
      : null;

  const extraButtons = isJAI
    ? [
        {
          text: t("editSubOrg"),
          handleClick: () => window.open(jaiEditURL, "_blank"),
        },
      ]
    : null;

  return (
    <>
      {message && (
        <div className={`hayley-flex ${showButtons ? (isMaximized ? "hayley-mb-4" : "hayley-mb-3") : "hayley-mb-3"}`}>
          {isUserMsg ? (
            isMaximized ? (
              <Icon iconType="DefaultUser" classes={"hayley-h-9 hayley-w-9 hayley-object-cover"} />
            ) : (
              ""
            )
          ) : (
            <>
              {isEmpty(artieLogo) && <div className="hayley-h-9 hayley-w-9">{renderMascot(mascotSettings, position, true, customSize, false)}</div>}

              {!isEmpty(artieLogo) && (
                <div className="hayley-z-10 hayley-flex hayley-h-9 hayley-w-9 hayley-items-center hayley-justify-center hayley-rounded-full hayley-border hayley-border-[#EEE]">
                  <img
                    id="staticImage"
                    src={artieLogo.includes("data:image/gif") ? staticImageData : artieLogo}
                    alt=""
                    className={`hayley-h-8 hayley-w-8 hayley-rounded-full hayley-object-cover`}
                  />
                </div>
              )}
            </>
          )}
          <div className="hayley-mx-3 hayley-max-w-[725px] hayley-rounded-lg hayley-rounded-tl-none hayley-bg-custom-7 !hayley-px-4 !hayley-py-2 hayley-text-sm hayley-text-secondary">
            {message}
          </div>
        </div>
      )}
      <div className={compMsgMarginClass}>
        <div className={`hayley-h-fit hayley-w-full`}>
          {badgeItems && badgeItems.length > 1 && (
            <BadgeSwitch
              module="photos"
              disabled={false}
              badgeItems={badgeItems}
              setSelectedItem={(id, item) => {
                setSelectedBadgeIdL(id);
                setSelectedBadgeItemL(item);
              }}
              selectedItem={selectedBadgeItemL}
            />
          )}

          <div>
            <div className={`hayley-grid hayley-w-full ${isMaximized ? "hayley-grid-cols-8" : "hayley-grid-cols-3"} hayley-gap-2`}>
              {dataArr.map((d, idx) => (
                <ProgressiveImage
                  key={d.id + idx}
                  classes={{
                    root: `hayley-w-full hayley-h-[100px] hayley-custom hayley-aspect-square hayley-cursor-pointer hayley-overflow-hidden hayley-border-[1px] hayley-border-solid hayley-border-[#EBEBEB]`,
                  }}
                  wrapperProps={{
                    onClick: () => handleClick(idx, d),
                    sx: { "&.hayley-custom:hover": { borderColor: `${color} !important` } },
                  }}
                  src={d.thumbnail_url}
                  alt={d.name}
                  selectedId={selectedBadgeIdL}
                  testId={`${selectedBadgeIdL}_image`}
                  meta={d}
                />
              ))}
            </div>
          </div>
        </div>
        {showButtons ? <ExtraOption from="photos" buttons={buttons} extraButtons={extraButtons} /> : null}
      </div>
    </>
  );
});

export default GalleryView;
